import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Brain, User, Buildings, Kanban, Hash, Trash, PencilSimple, Check, X, Plus, Robot } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { PageShell } from "@/components/PageShell";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";
import api, { errDetail } from "@/lib/api";

const SCOPES = [
  { key: "person", tk: "mem.people", label: "People", icon: User },
  { key: "organization", tk: "mem.orgs", label: "Organizations", icon: Buildings },
  { key: "project", tk: "mem.projects", label: "Projects", icon: Kanban },
  { key: "topic", tk: "mem.topics", label: "Topics", icon: Hash },
];
const SCOPE_WORD = { person: ["mem.scope_person", "person"], organization: ["mem.scope_organization", "organization"], project: ["mem.scope_project", "project"], topic: ["mem.scope_topic", "topic"] };

export default function MemoryPage() {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [scope, setScope] = useState("person");
  const [memories, setMemories] = useState([]);
  const [counts, setCounts] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newValidUntil, setNewValidUntil] = useState("");
  const [showExpired, setShowExpired] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([
        mbApi.get("/memories", { params: { scope, include_expired: showExpired } }),
        mbApi.get("/memories-summary"),
      ]);
      setMemories(m.data); setCounts(c.data);
    } catch (e) { toast.error(errDetail(e)); }
  }, [scope, showExpired, mbApi]);
  useEffect(() => { load(); }, [load]);

  const groups = memories.reduce((acc, m) => { (acc[m.key] = acc[m.key] || []).push(m); return acc; }, {});

  const saveEdit = async (m) => {
    try { await mbApi.put(`/memories/${m.id}`, { content: editContent }); setEditingId(null); toast.success(t("mem.updated", "Memory updated")); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const remove = async (m) => {
    try { await mbApi.delete(`/memories/${m.id}`); toast.success(t("mem.deleted", "Memory deleted")); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await mbApi.post("/memories", { scope, key: newKey, content: newContent, valid_until: newValidUntil || null });
      toast.success(t("mem.added", "Memory added")); setNewKey(""); setNewContent(""); setNewValidUntil(""); load();
    } catch (err) { toast.error(errDetail(err)); }
  };
  const renew = async (m) => {
    const d = window.prompt(t("mem.renew_prompt", "New expiry date (YYYY-MM-DD), leave empty for never:"), "");
    if (d === null) return;
    try { await mbApi.put(`/memories/${m.id}`, { content: m.content, valid_until: d.trim() }); toast.success(t("mem.renewed", "Memory renewed")); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <PageShell title={t("mem.title", "Memory")} subtitle={t("mem.subtitle", "Everything your Email Agent remembers — it uses this to personalize every draft and reply.")} testId="memory-page">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {SCOPES.map((s) => (
          <button key={s.key} data-testid={`memory-tab-${s.key}`} onClick={() => { setScope(s.key); setEditingId(null); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${scope === s.key ? "border-blue-600 text-gray-900" : "border-transparent text-gray-400 hover:text-gray-700"}`}>
            <s.icon size={15} /> {t(s.tk, s.label)}
            <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-px">{counts[s.key] ?? 0}</span>
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 pb-2 cursor-pointer">
          <input data-testid="memory-show-expired-toggle" type="checkbox" checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)} className="w-3.5 h-3.5 accent-blue-600" />
          {t("mem.show_expired", "show expired")}
        </label>
      </div>

      <form onSubmit={add} className="border border-gray-200 rounded-md p-4 mb-6">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-3"><Plus size={15} className="text-blue-600" /> {t("mem.teach", "Teach the agent something new ({scope})", { scope: t(...SCOPE_WORD[scope]) })}</div>
        <div className="flex gap-2">
          <input data-testid="memory-add-key-input" required value={newKey} onChange={(e) => setNewKey(e.target.value)}
            placeholder={scope === "person" ? "person@email.com" : scope === "organization" ? t("mem.ph_org", "Organization name") : scope === "project" ? t("mem.ph_project", "Project name") : t("mem.ph_topic", "Topic name")}
            className="w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input data-testid="memory-add-content-input" required value={newContent} onChange={(e) => setNewContent(e.target.value)}
            placeholder={t("mem.ph_content", "What should the agent remember?")}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input data-testid="memory-add-expiry-input" type="date" value={newValidUntil} onChange={(e) => setNewValidUntil(e.target.value)}
            title={t("mem.expiry_title", "Expires on (optional)")}
            className="w-40 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button data-testid="memory-add-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 transition-colors">{t("common.save", "Save")}</button>
        </div>
      </form>

      {Object.keys(groups).length === 0 && (
        <div data-testid="memory-empty" className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-gray-400">
          <Brain size={28} className="mx-auto mb-2 opacity-50" />
          {t("mem.empty", "No {scope} memories yet — chat with the agent or add one above.", { scope: t(...SCOPE_WORD[scope]) })}
        </div>
      )}

      <div className="space-y-5">
        {Object.entries(groups).map(([key, items]) => (
          <div key={key} data-testid={`memory-group-${key}`}>
            <h3 className="font-heading font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
              <Brain size={14} className="text-blue-600" /> {key}
              <span className="text-xs font-normal text-gray-400">{items.length > 1 ? t("mem.count", "{n} memories", { n: items.length }) : t("mem.count_one", "1 memory")}</span>
            </h3>
            <div className="space-y-1.5">
              {items.map((m) => (
                <div key={m.id} data-testid={`memory-card-${m.id}`} className="border border-gray-200 rounded-md px-3.5 py-2.5 flex items-start gap-3">
                  {editingId === m.id ? (
                    <div className="flex-1">
                      <textarea data-testid="memory-edit-input" value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={2}
                        className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <div className="flex gap-2 mt-1.5">
                        <button data-testid={`memory-save-${m.id}`} onClick={() => saveEdit(m)}
                          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded px-3 py-1.5 transition-colors"><Check size={12} /> {t("common.save", "Save")}</button>
                        <button data-testid={`memory-cancel-${m.id}`} onClick={() => setEditingId(null)}
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-2 py-1.5"><X size={12} /> {t("common.cancel", "Cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 leading-relaxed">{m.content}</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                        {m.auto && <span data-testid={`memory-auto-badge-${m.id}`} className="flex items-center gap-0.5 bg-blue-50 text-blue-600 rounded-full px-1.5 py-px font-medium"><Robot size={10} /> {t("mem.auto", "auto-learned")}</span>}
                        {m.expired && <span data-testid={`memory-expired-badge-${m.id}`} className="bg-gray-100 text-gray-500 rounded-full px-1.5 py-px font-medium uppercase">{t("mem.expired", "expired")}</span>}
                        {m.valid_until && !m.expired && <span data-testid={`memory-validuntil-${m.id}`} className="text-amber-600">{t("mem.valid_until", "valid until {date}", { date: m.valid_until })}</span>}
                        <span>{dayjs(m.created_at).format("MMM D, YYYY h:mm A")}</span>
                      </div>
                    </div>
                  )}
                  {editingId !== m.id && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      {m.expired && (
                        <button data-testid={`memory-renew-${m.id}`} onClick={() => renew(m)}
                          className="text-[11px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded px-2 py-1 transition-colors">{t("mem.renew", "renew")}</button>
                      )}
                      <button data-testid={`memory-edit-${m.id}`} onClick={() => { setEditingId(m.id); setEditContent(m.content); }}
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"><PencilSimple size={14} /></button>
                      <button data-testid={`memory-delete-${m.id}`} onClick={() => remove(m)}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1"><Trash size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}
